import { useCallback, useMemo, useState } from 'react';
import { Chip, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import type { GridColDef } from '@mui/x-data-grid';
import { usePageTitle } from '../../components/shell/AppShell';
import { RouteDataGrid } from '../../components/data-grid/RouteDataGrid';
import type { QuickFilter, RowAction } from '../../components/data-grid/RouteDataGrid';
import { fmtDateTime } from '../../utils/format';
import type { ExportColumn } from '../../utils/export';
import type { QrBatchSummary } from '../../api/types';
import { apiBaseUrl, TOKEN_STORAGE_KEY } from '../../auth/config';
import {
  useMyPermissions,
  useQrBatchesList,
} from '../../api/queries';
import { GenerateQrBatchDialog } from './GenerateQrBatchDialog';
import { useAlert } from '../../components/dialogs/DialogProvider';

/**
 * Stage-2 commissioning M2 — QR pair batches.
 *
 * ACES-only Phase 0 surface. Mints paired DEVICE+PACKAGING QR codes for
 * the Mac factory tool to print, lists batches, and exports the per-batch
 * pair data as CSV (the Mac tool drives the actual label printer).
 */
export default function QrBatchesPage() {
  usePageTitle('Site controller QR batches');
  const perms = useMyPermissions();
  const has = useCallback(
    (code: string) => perms.data?.codes?.includes(code) ?? false,
    [perms.data?.codes],
  );
  const alert = useAlert();

  const navigate = useNavigate();
  const { queryKey, listFn } = useQrBatchesList();
  const [generateOpen, setGenerateOpen] = useState(false);

  const columns = useMemo<GridColDef<QrBatchSummary>[]>(() => [
    { field: 'code', headerName: 'Batch', width: 200,
      renderCell: (p) => <code>{p.row.code}</code> },
    { field: 'generatedCount', headerName: 'Generated', width: 110,
      renderCell: (p) => p.row.generatedCount.toLocaleString() },
    { field: 'boundCount', headerName: 'Bound', width: 110,
      renderCell: (p) => `${p.row.boundCount.toLocaleString()} / ${p.row.generatedCount.toLocaleString()}` },
    { field: 'status', headerName: 'Status', width: 130,
      renderCell: (p) => <BatchStatusChip status={p.row.status} /> },
    { field: 'notes', headerName: 'Notes', flex: 1,
      renderCell: (p) => p.row.notes
        ? <Typography variant="body2">{p.row.notes}</Typography>
        : <Typography variant="body2" color="text.secondary">—</Typography> },
    { field: 'createdAt', headerName: 'Created', width: 180,
      valueFormatter: (value) => fmtDateTime(value) },
  ], []);

  const quickFilters = useMemo<QuickFilter<QrBatchSummary>[]>(() => [
    { label: 'All',         filter: () => true },
    { label: 'Printed',     filter: (b) => b.status === 'printed' },
    { label: 'Voided',      filter: (b) => b.status === 'voided' },
  ], []);

  const exportColumns = useMemo<ExportColumn<QrBatchSummary>[]>(() => [
    { key: 'code',           label: 'Batch code' },
    { key: 'requestedCount', label: 'Requested' },
    { key: 'generatedCount', label: 'Generated' },
    { key: 'boundCount',     label: 'Bound' },
    { key: 'status',         label: 'Status' },
    { key: 'notes',          label: 'Notes' },
    { key: 'createdAt',      label: 'Created', format: (v) => fmtDateTime(v) },
  ], []);

  const actions = useMemo<RowAction<QrBatchSummary>[]>(() => [
    { label: 'Open',         onClick: (row) => navigate(`/factory/qr-batches/${row.id}`) },
    { label: 'Print',        onClick: (row) => navigate(`/factory/qr-batches/${row.id}/print`) },
    { label: 'Download CSV', onClick: (row) => downloadCsv(row.id, row.code, alert) },
  ], [navigate, alert]);

  if (!has('provisioning.factory.read')) {
    return (
      <>
        <Typography color="text.secondary">
          You don't have permission to view factory QR batches.
        </Typography>
      </>
    );
  }

  return (
    <>
      <Stack spacing={2}>
        <Typography variant="h4">Site controller QR batches</Typography>
        <Typography variant="body2" color="text.secondary">
          ACES-only Phase 0 surface. Each batch is a run of paired DEVICE +
          PACKAGING QR codes signed with the SC factory key. The Mac
          commissioning tool downloads a batch's CSV to drive label
          printing, then binds each printed pair to a CM5 at the bench.
        </Typography>

        <RouteDataGrid<QrBatchSummary>
          entityName="QR batches"
          queryKey={queryKey}
          listFn={listFn}
          columns={columns}
          searchFields={[{ kind: 'text', key: 'code', label: 'Batch code' }]}
          quickFilters={quickFilters}
          exportColumns={exportColumns}
          reportTitle="ARMS — Site controller QR batches"
          actions={actions}
          primaryAction={
            has('provisioning.factory.mint')
              ? { label: '+ Generate batch', onClick: () => setGenerateOpen(true) }
              : undefined
          }
          onRowClick={(row) => navigate(`/factory/qr-batches/${row.id}`)}
          emptyMessage="No QR batches yet — generate one to get started."
        />

        <GenerateQrBatchDialog
          open={generateOpen}
          onClose={() => setGenerateOpen(false)}
        />
      </Stack>
    </>
  );
}

function BatchStatusChip({ status }: { status: string }) {
  const color =
    status === 'printed' ? 'success' :
    status === 'created' ? 'info' :
    status === 'voided'  ? 'error' :
    'default';
  return <Chip size="small" label={status} color={color as 'success' | 'info' | 'error' | 'default'} />;
}

async function downloadCsv(
  batchId: string, batchCode: string,
  alert: (opts: { title: string; message: string; severity?: 'info' | 'warning' | 'error' | 'success' }) => Promise<void>,
) {
  // Pull through fetch with the access token then offer as a blob — saves
  // exposing the bearer token in the URL of an <a> click.
  const url = `${apiBaseUrl}/api/v1/factory/qr-batches/${batchId}/export.csv`;
  const token = sessionStorage.getItem(TOKEN_STORAGE_KEY);
  if (!token) {
    await alert({ title: 'Not signed in', message: 'Sign in and retry the download.', severity: 'warning' });
    return;
  }
  try {
    const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) {
      const text = await r.text();
      throw new Error(`HTTP ${r.status}: ${text || r.statusText}`);
    }
    const blob = await r.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `sc-qr-batch-${batchCode}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(a.href);
  } catch (e) {
    await alert({ title: 'Download failed', message: (e as Error).message, severity: 'error' });
  }
}

